import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { z } from "zod";
import { authService } from "../../services/auth.service";
import { Lock, ArrowLeft, ArrowRight } from "lucide-react";
import logo from "../../assets/logo.png";

const resetPasswordSchema = z
  .object({
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type ResetPasswordData = z.infer<typeof resetPasswordSchema>;

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const onSubmit = async (data: ResetPasswordData) => {
    if (!token) {
      setError("Invalid or missing reset token");
      return;
    }

    setError("");
    setIsLoading(true);

    try {
      await authService.resetPassword(token, data.password);
      navigate("/login?reset=success");
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
        "Failed to reset password. The link may have expired.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 relative overflow-hidden">
        {/* Background blobs for elite feel even on error page */}
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-indigo-500/5 blur-[100px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-red-500/5 blur-[100px]" />

        <div className="max-w-md w-full p-10 glass rounded-[40px] text-center border-slate-100 shadow-2xl space-y-8 animate-fade-in relative z-10">
          <div className="w-20 h-20 rounded-[32px] bg-red-500 flex items-center justify-center mx-auto shadow-xl shadow-red-500/20">
            <Lock className="w-10 h-10 text-white stroke-[2.5]" />
          </div>
          <div className="space-y-4">
            <h2 className="text-3xl font-black text-slate-900 tracking-tight">Invalid Link</h2>
            <p className="text-slate-500 font-bold text-sm leading-relaxed">
              This synchronization token has either expired or been invalidated. Please request a fresh transmission.
            </p>
          </div>
          <Link
            to="/forgot-password"
            className="block w-full py-5 bg-slate-950 text-white text-xs font-black uppercase tracking-widest rounded-2xl hover:bg-black transition-all shadow-xl shadow-slate-950/10 active:scale-95"
          >
            Request New Link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white overflow-hidden">
      {/* Left Side - Immersive Hero Section with Blue/Teal Branding */}
      <div className="hidden lg:flex flex-col justify-between p-16 relative overflow-hidden bg-blue-600 text-white rounded-tr-[50px] rounded-br-[50px]">
        {/* Elite Aurora Background - Blue/Teal Theme */}

        {/* Elite Aurora Background - Blue/Teal Theme */}
        <div className="absolute inset-0 z-0 text-white">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600" />
          <div className="absolute top-[-10%] left-[-10%] w-[100%] h-[100%] rounded-full blur-[120px] animate-pulse" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[80%] h-[80%] rounded-full bg-white/10 blur-[100px] animate-pulse delay-700" />
          <div className="absolute top-[20%] left-[10%] w-[60%] h-[60%] rounded-full bg-blue-400/20 blur-[110px] animate-pulse delay-1000" />
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-soft-light" />
        </div>

        {/* Branding - Elite Logo Pill */}
        <div className="relative z-10">
          <div className="flex items-center gap-3.5 bg-white backdrop-blur-2xl py-3 px-6 rounded-2xl border border-white/20 shadow-2xl w-fit group hover:bg-white/20 transition-all duration-300">
            <div className="flex flex-col text-white">
              <span className="text-5xl font-black tracking-tight leading-none">
                {/* MediCorePay */}
                <span className="text-blue-800">Medi</span>
                <span className="text-red-700">Core</span>
                <span className="text-teal-700">Pay</span>
              </span>
              <span className="text-[10px] uppercase tracking-widest text-blue-100 font-bold mt-1">Enterprise POS</span>
            </div>
          </div>
        </div>

        <div className="relative z-10 max-w-lg space-y-10">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white text-xs font-bold tracking-wider uppercase">
              <span className="flex h-1.5 w-1.5 rounded-full bg-teal-300 animate-ping" />
              Active Reset Session
            </div>
            <h1 className="text-6xl font-black leading-[1.1] tracking-tighter text-white">
              Secure Your <br />
              <span className="text-teal-200">Environment.</span>
            </h1>
            <p className="text-xl text-blue-50 font-light leading-relaxed max-w-md">
              Create a cryptographic-grade password to insulate your pharmacy's operational data. Integrity is our mandate.
            </p>
          </div>

          <div className="flex items-center gap-5 p-5 rounded-[32px] w-fit border-white/10 bg-white/10 backdrop-blur-md">
            <div className="w-12 h-12 rounded-2xl bg-teal-400 flex items-center justify-center shadow-lg shadow-teal-400/20">
              <Lock className="w-6 h-6 text-white stroke-[3]" />
            </div>
            <div>
              <p className="text-sm font-black text-white">SHA-512 Hashing</p>
              <p className="text-xs text-blue-100 font-bold">End-to-End Encrypted Handshake</p>
            </div>
          </div>
        </div>

        <div className="relative z-10 flex justify-between items-end">
          <p className="text-sm text-blue-100 font-medium">© 2026 MediCorePay Global</p>
          <div className="flex gap-8">
            <a href="#" className="text-blue-100 hover:text-white transition-colors text-sm font-semibold">Audit</a>
            <a href="#" className="text-blue-100 hover:text-white transition-colors text-sm font-semibold">ISO 27001</a>
          </div>
        </div>
      </div>

      {/* Right Side - Reset Form */}
      <div className="flex flex-col items-center justify-center p-6 sm:p-12 w-full animate-fade-in relative bg-white overflow-y-auto">
        <div className="w-full max-w-[440px] space-y-12">
          {/* Logo above Title - X-Large format */}
          <div className="flex flex-col items-center mb-8">
            <img
              src={logo}
              alt="MediCorePay Logo"
              className="w-25 h-25 object-contain max-w-[200px] mb-8 group hover:scale-105 transition-all duration-500"
            />
            <div className="space-y-4 text-center">
              <div className="inline-block p-1 bg-gradient-to-r from-blue-500 to-teal-400 rounded-full mb-2">
                <div className="bg-white rounded-full px-4 py-1 text-[10px] font-black uppercase tracking-widest text-blue-600">
                  Credential Override
                </div>
              </div>
              <h2 className="text-5xl font-black text-slate-950 tracking-tighter leading-none">Finalize Reset</h2>
              <p className="text-slate-500 font-bold text-lg">
                Deploy a new security key for your enterprise account.
              </p>
            </div>
          </div>

          <div className="glass p-10 rounded-[40px] border-slate-100 shadow-2xl shadow-indigo-500/5">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              {error && (
                <div className="p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl text-[11px] font-black uppercase tracking-wider flex items-center gap-3 animate-fade-in">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  {error}
                </div>
              )}

              <div className="space-y-6">
                <div className="space-y-3">
                  <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">
                    New Security Key
                  </label>
                  <div className="relative group">
                    <input
                      {...register("password")}
                      type="password"
                      className="block w-full px-6 py-5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-500/20 focus:ring-4 focus:ring-indigo-500/5 transition-all duration-300 outline-none text-slate-950 placeholder-slate-400 font-bold"
                      placeholder="••••••••"
                    />
                    <div className="absolute inset-y-0 right-0 pr-6 flex items-center pointer-events-none text-slate-300 group-focus-within:text-indigo-500 transition-colors">
                      <Lock className="h-5 w-5 stroke-[2.5]" />
                    </div>
                  </div>
                  {errors.password && (
                    <p className="text-xs text-red-500 font-bold ml-1 flex items-center gap-1.5 animate-slide-up">
                      <span className="w-1 h-1 rounded-full bg-red-500" />
                      {errors.password.message}
                    </p>
                  )}
                </div>

                <div className="space-y-3">
                  <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">
                    Confirm Key Activation
                  </label>
                  <div className="relative group">
                    <input
                      {...register("confirmPassword")}
                      type="password"
                      className="block w-full px-6 py-5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-500/20 focus:ring-4 focus:ring-indigo-500/5 transition-all duration-300 outline-none text-slate-950 placeholder-slate-400 font-bold"
                      placeholder="••••••••"
                    />
                    <div className="absolute inset-y-0 right-0 pr-6 flex items-center pointer-events-none text-slate-300 group-focus-within:text-indigo-500 transition-colors">
                      <Lock className="h-5 w-5 stroke-[2.5]" />
                    </div>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-xs text-red-500 font-bold ml-1 flex items-center gap-1.5 animate-slide-up">
                      <span className="w-1 h-1 rounded-full bg-red-500" />
                      {errors.confirmPassword.message}
                    </p>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full relative group py-5 bg-slate-950 hover:bg-black text-white font-black text-[13px] uppercase tracking-[0.2em] rounded-2xl shadow-2xl shadow-slate-950/20 active:scale-[0.98] transition-all duration-300 overflow-hidden disabled:opacity-50"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="relative z-10 flex items-center justify-center gap-3">
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Provisioning...
                    </>
                  ) : (
                    <>
                      Override Password
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1.5 transition-transform stroke-[3]" />
                    </>
                  )}
                </span>
              </button>
            </form>
          </div>

          <div className="text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-3 text-slate-400 font-black text-[11px] uppercase tracking-widest hover:text-slate-900 transition-all group active:scale-95"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform stroke-[3]" />
              Return to Authentication
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
